/**
 * 絵コンテモードのシーンの「モーションで上書き」(docs/features/brand-cut-motion-graphics-plan.md §10.13 4.) の切替。
 *
 * `scenes.video_input_mode` を null ↔ 'motion' に更新する (SceneFrameCarousel.handleVideoInputModeChange と同じ保存 +
 * ローカル state 反映パターン。絵コンテでは motion 以外の明示値は出さない)。入口は 2 つ:
 * - シーンの 3 点メニュー「モーションで上書き」(SceneItem) → 'motion'
 * - モーションカードの × (StoryboardMotionCard) → null (絵コンテ動画のまま)
 * シナリオ生成時に AI がモーション向きと判定したシーンは backend が初期値 'motion' を書くので、最初からカードが出る。
 */
import { useSceneHandlers } from './SceneHandlersContext';
import { useToast } from '../../../../hooks/useToast';
import type { Scenario } from '../types';
import {
  storyboardSceneMode,
  storyboardSceneModePatch,
  type StoryboardSceneMode,
} from '../sceneItem/storyboardMotionLogic';

export interface StoryboardMotionModeState {
  mode: StoryboardSceneMode;
  isMotion: boolean;
  /** 保存中 (このシーンの更新が走っている) */
  pending: boolean;
  setMode: (next: StoryboardSceneMode) => Promise<void>;
}

export function useStoryboardMotionMode(
  scene: Scenario['scenes'][number],
  sceneKey: string,
): StoryboardMotionModeState {
  const {
    updateSceneMutation,
    lastResult,
    setLastResult,
    pendingContinuityScenes,
    setPendingContinuityScenes,
    updateSceneVideoSettings,
  } = useSceneHandlers();
  const toast = useToast();

  const mode = storyboardSceneMode(scene);
  const pending = pendingContinuityScenes.has(sceneKey) || !!updateSceneMutation?.isPending;

  const setMode = async (next: StoryboardSceneMode) => {
    if (next === mode || pending) return;
    const payload = storyboardSceneModePatch(next);
    setPendingContinuityScenes((prev) => new Set(prev).add(sceneKey));
    try {
      await updateSceneMutation.mutateAsync({ sceneId: scene.id, payload });
      if (lastResult) {
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) => (sc.id === scene.id ? { ...sc, ...payload } : sc)),
          })),
        } : prev));
      }
      // 旧トグル由来のローカル値が残らないように (SceneFrameCarousel.handleVideoInputModeChange と同じ)
      updateSceneVideoSettings(sceneKey, { linkPreviousVideo: undefined });
    } catch {
      toast.error('更新に失敗しました');
    } finally {
      setPendingContinuityScenes((prev) => {
        if (!prev.has(sceneKey)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(sceneKey);
        return nextSet;
      });
    }
  };

  return { mode, isMotion: mode === 'motion', pending, setMode };
}
